import { NavLink, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { LayoutDashboard, Users, ClipboardCheck, BarChart3, MessageSquare, Settings, LogOut } from "lucide-react";
import { logout, getCurrentUser } from "../api";
import { useEffect, useState } from "react";
import type { PortalSettings } from "../types";

const BASE_TABS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/members", label: "Members", icon: Users },
  { to: "/checkins", label: "Check-Ins", icon: ClipboardCheck },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/chat", label: "Chat", icon: MessageSquare },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const isOwner = user?.role === "Owner";
  const [settings, setSettings] = useState<PortalSettings | null>(null);

  useEffect(() => {
    // Load portal settings for dynamic branding
    import("../api").then(({ api }) => {
      api.getPortalSettings().then((r) => setSettings(r.settings)).catch(() => {});
    });
  }, []);

  const primary = settings?.brand_primary || "#5f1b2c";
  const secondary = settings?.brand_secondary || "#e2c4b6";
  const bg = settings?.brand_bg || "#ffffff";

  const handleLogout = () => {
    logout();
    navigate("/login");
    window.location.reload();
  };

  const tabs = isOwner ? [...BASE_TABS, { to: "/settings", label: "Settings", icon: Settings }] : BASE_TABS;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: bg }}>
      {/* Top bar */}
      <header style={{ background: primary }} className="px-4 md:px-6 py-3 flex items-center gap-4 md:gap-8 sticky top-0 z-50">
        <div className="flex items-center gap-2 mr-2 md:mr-4">
          {settings?.logo_url ? (
            <img src={settings.logo_url} alt="" className="w-8 h-8 md:w-9 md:h-9 rounded-lg object-cover" />
          ) : (
            <img src="/fgnkd-logo.png" alt="FGNKD" className="w-8 h-8 md:w-9 md:h-9 rounded-lg object-cover" />
          )}
          <span className="text-lg md:text-xl font-bold tracking-tight text-white">
            FGNKD
          </span>
        </div>
        <nav className="flex gap-1 overflow-x-auto flex-1">
          {tabs.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                clsx(
                  "flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap",
                  isActive
                    ? "bg-white/20 text-white"
                    : "text-white/70 hover:text-white hover:bg-white/10"
                )
              }
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
            </NavLink>
          ))}
        </nav>
        {/* User info + logout */}
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs text-white/60 hidden md:inline">{user?.name}</span>
          <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full">
        {children}
      </main>
    </div>
  );
}